import { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { useSelector } from "react-redux";

import {
  getAddresses,
  createAddress,
  updateAddress,
  deleteAddress,
  setPrimaryAddress,
} from "../../api/savedAddressApi";
import AddressAutocomplete from "./AddressAutocomplete";
import { isValidDeliveryPhone, savedAddressLatLng } from "../../utils/shippingValidation";

// ── Self-contained saved-address picker ───────────────────────────────────
// Fetches the authenticated customer's address book on mount and lets them
// pick a saved delivery address (or add a new one). On selection it calls
// `onSelect(address)` with the full saved-address object, which always carries
// `latLng:{lat,lng}` — the field every vendor's place-order screen reads for
// the delivery-coverage gate + fee. Auth/loading/empty states are handled so
// the host shipping screen can fall back to manual entry when there is nothing
// to show.
//
// Flat styling, green `#03660c` accent, consistent with the storefront cards.

const ACCENT = "#03660c";

const Wrap = styled.div`
  margin: 0 0 14px 0;
  font-family: "Poppins", sans-serif;
`;

const Heading = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const HeadingTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #14282d;
`;

const LinkBtn = styled.button`
  border: none;
  background: none;
  color: ${ACCENT};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 2px;
  &:hover {
    text-decoration: underline;
  }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Card = styled.div`
  border: 1.5px solid
    ${(p) => (p.$invalid ? "#e4b65b" : p.$selected ? ACCENT : "#e3e7e8")};
  background: ${(p) => (p.$invalid ? "#fffaf0" : p.$selected ? "#f1f8f1" : "#fff")};
  border-radius: 10px;
  padding: 10px 12px;
  cursor: ${(p) => (p.$invalid ? "default" : "pointer")};
  transition: border-color 0.14s ease, background 0.14s ease;
  &:hover {
    border-color: ${(p) => (p.$invalid ? "#d39a2e" : ACCENT)};
  }
`;

const CardTop = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 2px;
`;

const Label = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #14282d;
`;

const PrimaryBadge = styled.span`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #fff;
  background: ${ACCENT};
  border-radius: 4px;
  padding: 2px 6px;
`;

const InvalidBadge = styled(PrimaryBadge)`
  background: #a76700;
`;

const AddressText = styled.div`
  font-size: 12px;
  color: #51626a;
  line-height: 1.35;
`;

const Phone = styled.div`
  font-size: 11px;
  color: #7a8a90;
  margin-top: 2px;
`;

const CardActions = styled.div`
  display: flex;
  gap: 14px;
  margin-top: 6px;
`;

const ActionBtn = styled.button`
  border: none;
  background: none;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  color: ${(p) => (p.$danger ? "#c0392b" : ACCENT)};
  &:disabled {
    color: #b6bfc2;
    cursor: not-allowed;
  }
  &:hover:not(:disabled) {
    text-decoration: underline;
  }
`;

const Muted = styled.div`
  font-size: 12px;
  color: #7a8a90;
  padding: 8px 0;
`;

const ErrorText = styled.div`
  font-size: 12px;
  color: #c0392b;
  padding: 6px 0;
`;

// ── Add-new form ──
const FormCard = styled.div`
  border: 1px dashed #c9d2d4;
  border-radius: 10px;
  padding: 12px;
  margin-top: 10px;
  background: #fafbfb;
`;

const Field = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 9px 10px;
  margin: 0 0 8px 0;
  border: 1px solid #d6dcde;
  border-radius: 8px;
  font-size: 13px;
  outline: none;
  &:focus {
    border-color: ${ACCENT};
  }
`;

const FormRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 4px;
`;

const PrimaryBtn = styled.button`
  border: 1px solid ${ACCENT};
  background: ${ACCENT};
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  border-radius: 8px;
  padding: 9px 16px;
  cursor: pointer;
  &:disabled {
    background: #b6bfc2;
    border-color: #b6bfc2;
    cursor: not-allowed;
  }
`;

const GhostBtn = styled.button`
  border: 1px solid #d6dcde;
  background: #fff;
  color: #51626a;
  font-size: 13px;
  font-weight: 600;
  border-radius: 8px;
  padding: 9px 16px;
  cursor: pointer;
`;

const CheckRow = styled.label`
  /* Doubled specificity (&&) so a global \`label\` rule can't re-center this;
     keep the checkbox + text as a left-aligned row. */
  && {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    gap: 6px;
    text-align: left;
  }
  /* A global "input { width: 100% }" (leaked app-wide from the card-form CSS)
     stretches the checkbox and pushes the label to the far right. Pin the
     checkbox to a fixed size so the row stays a tight, left-aligned pair. */
  input[type="checkbox"] {
    width: 16px;
    height: 16px;
    flex: 0 0 auto;
    margin: 0;
    padding: 0;
  }
  font-size: 12px;
  color: #51626a;
  margin: 2px 0 8px 0;
  cursor: pointer;
`;

const SavedAddressPicker = ({
  onSelect,
  onDeselect,
  selectedId,
  // When the shopper has ALREADY made an explicit delivery choice (e.g. typed
  // and picked an area in the location modal seconds ago), the saved primary
  // must not silently overwrite it. The caller decides — default keeps the old
  // behaviour for screens with no other source of address.
  autoSelect = true,
}) => {
  const currentUser = useSelector((state) => state.user.currentUser);

  const [addresses, setAddresses] = useState([]);
  /* How many the account may keep, READ FROM THE SERVER rather than repeated
     here. The cap is enforced in savedAddressController and travels with the
     list, so a change there reaches this screen on the next request instead of
     on the next release. Null until the list has loaded, and null hides
     nothing: a limit we have not been told is not a limit to enforce. */
  const [limit, setLimit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [chosenId, setChosenId] = useState(selectedId || null);

  // When the host passes `selectedId`, treat it as the source of truth so the
  // highlighted card can never diverge from the host's form state (e.g. after
  // the customer switches to a manually-typed address the host clears it).
  useEffect(() => {
    if (selectedId !== undefined) setChosenId(selectedId || null);
  }, [selectedId]);

  // Add-new form state
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    label: "",
    address: "",
    recipientPhone: "",
    isPrimary: false,
  });
  const autoSelectRef = useRef(autoSelect);
  useEffect(() => {
    autoSelectRef.current = autoSelect;
  }, [autoSelect]);
  const [coords, setCoords] = useState(null); // { lat, lng }
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    setError("");
    try {
      const data = await getAddresses();
      const list = (data && data.addresses) || [];
      setAddresses(list);
      const serverLimit = Number(data?.limit);
      setLimit(Number.isFinite(serverLimit) && serverLimit > 0 ? serverLimit : null);
      // Only addresses with a valid point may become authoritative checkout
      // state. Legacy text-only entries remain visible with a repair action.
      const valid = list.filter((address) => savedAddressLatLng(address));
      if (valid.length && !chosenId && autoSelectRef.current) {
        const primary = valid.find((a) => a.isPrimary) || valid[0];
        setChosenId(primary._id);
        onSelect(primary);
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        setAddresses([]);
      } else {
        setError("Could not load your saved addresses.");
      }
    } finally {
      setLoading(false);
    }
    // `chosenId`/`onSelect` intentionally excluded: we only auto-select on the
    // initial fetch, not on every selection change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  useEffect(() => {
    load();
  }, [load]);

  const handlePick = (addr) => {
    if (!savedAddressLatLng(addr)) return;
    setChosenId(addr._id);
    onSelect(addr);
  };

  const handleRepair = (e, addr) => {
    e.stopPropagation();
    setEditingId(addr._id);
    setForm({
      label: addr.label || "",
      address: addr.address || addr.description || "",
      recipientPhone: addr.recipientPhone || "",
      isPrimary: Boolean(addr.isPrimary),
    });
    setCoords(null);
    setError("");
    setShowForm(true);
  };

  const handleSetPrimary = async (e, id) => {
    e.stopPropagation();
    setBusyId(id);
    try {
      await setPrimaryAddress(id);
      await load();
    } catch (err) {
      setError("Could not update primary address.");
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    setBusyId(id);
    try {
      await deleteAddress(id);
      setAddresses((prev) => prev.filter((a) => a._id !== id));
      if (chosenId === id) {
        setChosenId(null);
        // Tell the host its form is now holding a deleted address so it can
        // clear/reset — otherwise stale data ships with the order.
        if (onDeselect) onDeselect();
      }
    } catch (err) {
      setError("Could not delete that address.");
    } finally {
      setBusyId(null);
    }
  };

  const resetForm = () => {
    setForm({ label: "", address: "", recipientPhone: "", isPrimary: false });
    setCoords(null);
    setEditingId(null);
    setShowForm(false);
  };

  const canSave =
    form.label.trim() &&
    form.address.trim() &&
    isValidDeliveryPhone(form.recipientPhone) &&
    Boolean(coords);

  const handleCreate = async () => {
    if (!canSave) return;
    setSaving(true);
    setError("");
    try {
      const payload = {
        label: form.label.trim(),
        address: form.address.trim(),
        description: form.address.trim(),
        latLng: coords,
        recipientPhone: form.recipientPhone.trim(),
        isPrimary: form.isPrimary,
      };
      const data = editingId
        ? await updateAddress(editingId, payload)
        : await createAddress(payload);
      const created = data && data.address;
      resetForm();
      await load();
      // Auto-select the freshly created/repaired address.
      if (created) {
        setChosenId(created._id);
        onSelect(created);
      }
    } catch (err) {
      /* THE SERVER'S OWN WORDS WHEN IT HAS THEM. A refusal the server took the
         trouble to explain is actionable: the saved-address limit answers 409
         with "You can keep up to 3 saved addresses. Delete one you no longer
         use, then add this one." Replacing that with "Could not save the new
         address" throws away the only part the buyer can act on, which is the
         same fault the mail-configuration message was fixed for. */
      const serverMessage = err?.response?.data?.message;
      setError(
        serverMessage ||
          (editingId
            ? "Could not update that address."
            : "Could not save the new address."),
      );
    } finally {
      setSaving(false);
    }
  };

  // Not logged in → render nothing so the host shows its manual form only.
  if (!currentUser) return null;

  /* `>=` rather than `===`: an account that already holds more than the cap,
     from before it existed, must not be topped up further either. Mirrors the
     server's own comparison. */
  const atAddressLimit = limit !== null && addresses.length >= limit;

  return (
    <Wrap>
      <Heading>
        <HeadingTitle>Saved addresses</HeadingTitle>
        {!showForm && !atAddressLimit && (
          <LinkBtn type="button" onClick={() => setShowForm(true)}>
            + Add new address
          </LinkBtn>
        )}
        {!showForm && atAddressLimit && (
          /* Say why the control is gone. A button that silently disappears
             reads as a bug; the server refuses this case anyway, so the
             screen may as well explain it before the buyer tries. */
          <Muted>
            {`You can keep ${limit}. Delete one to add another.`}
          </Muted>
        )}
      </Heading>

      {error && <ErrorText>{error}</ErrorText>}

      {loading ? (
        <Muted>Loading your addresses…</Muted>
      ) : addresses.length === 0 && !showForm ? (
        <Muted>
          No saved addresses yet. Add one above, or enter an address manually
          below.
        </Muted>
      ) : (
        <List>
          {addresses.map((addr) => {
            const validCoordinates = Boolean(savedAddressLatLng(addr));
            return (
              <Card
                key={addr._id}
                $selected={validCoordinates && chosenId === addr._id}
                $invalid={!validCoordinates}
                onClick={() => handlePick(addr)}
                role={validCoordinates ? "button" : undefined}
                tabIndex={validCoordinates ? 0 : undefined}
                onKeyDown={(event) => {
                  if (validCoordinates && (event.key === "Enter" || event.key === " ")) {
                    event.preventDefault();
                    handlePick(addr);
                  }
                }}
              >
              <CardTop>
                <Label>{addr.label || "Address"}</Label>
                {addr.isPrimary && <PrimaryBadge>Primary</PrimaryBadge>}
                {!validCoordinates && <InvalidBadge>Update pin</InvalidBadge>}
              </CardTop>
              <AddressText>{addr.address || addr.description}</AddressText>
              {addr.recipientPhone && <Phone>📞 {addr.recipientPhone}</Phone>}
              <CardActions>
                {!validCoordinates && (
                  <ActionBtn type="button" onClick={(e) => handleRepair(e, addr)}>
                    Update location pin
                  </ActionBtn>
                )}
                {!addr.isPrimary && (
                  <ActionBtn
                    type="button"
                    disabled={busyId === addr._id}
                    onClick={(e) => handleSetPrimary(e, addr._id)}
                  >
                    Set primary
                  </ActionBtn>
                )}
                <ActionBtn
                  type="button"
                  $danger
                  disabled={busyId === addr._id}
                  onClick={(e) => handleDelete(e, addr._id)}
                >
                  Delete
                </ActionBtn>
              </CardActions>
            </Card>
            );
          })}
        </List>
      )}

      {showForm && (
        <FormCard>
          <Field
            placeholder="Label (e.g. Home, Office)"
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
          />
          <AddressAutocomplete
            value={form.address}
            onChange={(v) => {
              setForm((f) => ({ ...f, address: v }));
              setCoords(null); // require re-resolve when the text changes
            }}
            onResolved={({ latLng, formatted }) => {
              setCoords(latLng);
              setForm((f) => ({ ...f, address: formatted }));
            }}
            onError={() =>
              setError("We couldn’t locate that address. Retry or choose another suggestion.")
            }
          />
          {form.address && !coords && (
            <Muted>Pick a suggestion so we can capture coordinates.</Muted>
          )}
          <Field
            placeholder="Recipient phone (e.g. 0700123123)"
            value={form.recipientPhone}
            type="tel"
            onChange={(e) =>
              setForm({ ...form, recipientPhone: e.target.value })
            }
          />
          <CheckRow>
            <input
              type="checkbox"
              checked={form.isPrimary}
              onChange={(e) =>
                setForm({ ...form, isPrimary: e.target.checked })
              }
            />
            Set as my primary address
          </CheckRow>
          <FormRow>
            <PrimaryBtn
              type="button"
              disabled={!canSave || saving}
              onClick={handleCreate}
            >
              {saving ? "Saving…" : editingId ? "Update address" : "Save address"}
            </PrimaryBtn>
            <GhostBtn type="button" onClick={resetForm}>
              Cancel
            </GhostBtn>
          </FormRow>
        </FormCard>
      )}
    </Wrap>
  );
};

SavedAddressPicker.propTypes = {
  // Called with the chosen saved-address object: { _id, label, address,
  // description, latLng:{lat,lng}, recipientPhone, isPrimary, ... }.
  onSelect: PropTypes.func.isRequired,
  // false → never auto-select the primary; the caller already has a chosen
  // destination and this picker is only an alternative.
  autoSelect: PropTypes.bool,
  // Optional: called when the currently-selected address is deleted, so the
  // host can clear form state that mirrored it.
  onDeselect: PropTypes.func,
  selectedId: PropTypes.string,
};

export default SavedAddressPicker;
